import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { type Dispatch, type SetStateAction, useState } from "react";
import { createPortal } from "react-dom";
import type { ListStatuses } from "~/types/api";
import {
  type StatusContainerId,
  getContainer,
  isContainerId,
  statusCollisionDetection,
} from "./dnd";
import { StatusItemPreview } from "./status-item/status-item-preview";
import { StatusTable } from "./status-table";
import { TransferButtons } from "./transfer-buttons";

type Props = {
  availableStatuses: ListStatuses[];
  chosenStatuses: ListStatuses[];
  selectedAvailableItems: number[];
  selectedChosenItems: number[];
  setSelectedAvailableItems: Dispatch<SetStateAction<number[]>>;
  setSelectedChosenItems: Dispatch<SetStateAction<number[]>>;
  onMove: (codes: number[], to: StatusContainerId) => void;
  onReorder?: (reordered: ListStatuses[]) => void;
  availableTitle: string;
  chosenTitle: string;
  availableEmptyPlaceholder: string;
  chosenEmptyPlaceholder: string;
};

type ActiveDrag = { code: number; container: StatusContainerId };

export const StatusTransferList = ({
  availableStatuses,
  chosenStatuses,
  selectedAvailableItems,
  selectedChosenItems,
  setSelectedAvailableItems,
  setSelectedChosenItems,
  onMove,
  onReorder,
  availableTitle,
  chosenTitle,
  availableEmptyPlaceholder,
  chosenEmptyPlaceholder,
}: Props) => {
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const listFor = (c: StatusContainerId) =>
    c === "available" ? availableStatuses : chosenStatuses;
  const selectionFor = (c: StatusContainerId) =>
    c === "available" ? selectedAvailableItems : selectedChosenItems;
  const setSelectionFor = (c: StatusContainerId) =>
    c === "available" ? setSelectedAvailableItems : setSelectedChosenItems;

  const toggle = (c: StatusContainerId) => (code: number) =>
    setSelectionFor(c)((prev) =>
      prev.includes(code) ? prev.filter((id) => id !== code) : [...prev, code]
    );

  const selectedInListOrder = (c: StatusContainerId) => {
    const selection = selectionFor(c);
    return listFor(c)
      .filter((s) => selection.includes(s.code))
      .map((s) => s.code);
  };

  const getDragGroup = (code: number, from: StatusContainerId) =>
    selectionFor(from).includes(code) ? selectedInListOrder(from) : [code];

  const move = (codes: number[], from: StatusContainerId) => {
    if (codes.length === 0) {
      return;
    }
    onMove(codes, from === "available" ? "selected" : "available");
    setSelectionFor(from)((prev) => prev.filter((c) => !codes.includes(c)));
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    const container = getContainer(active);
    if (container) {
      setActiveDrag({ code: Number(active.id), container });
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveDrag(null);
    if (!over) {
      return;
    }

    const from = getContainer(active);
    const to = getContainer(over);
    if (!from || !to) {
      return;
    }

    const activeCode = Number(active.id);

    if (from !== to) {
      move(getDragGroup(activeCode, from), from);
      return;
    }

    if (onReorder && to === "selected" && !isContainerId(over.id) && active.id !== over.id) {
      const oldIndex = chosenStatuses.findIndex((s) => s.code === activeCode);
      const newIndex = chosenStatuses.findIndex((s) => s.code === Number(over.id));
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }
      onReorder(
        arrayMove(chosenStatuses, oldIndex, newIndex).map((s, i) => ({ ...s, sortOrder: i }))
      );
    }
  };

  const activeStatus = activeDrag
    ? listFor(activeDrag.container).find((s) => s.code === activeDrag.code)
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={statusCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDrag(null)}
    >
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4">
        <StatusTable
          id="available"
          title={availableTitle}
          emptyPlaceholder={availableEmptyPlaceholder}
          statuses={availableStatuses}
          selectedItems={selectedAvailableItems}
          toggleSelection={toggle("available")}
        />
        <TransferButtons
          onMoveToSelected={() => move(selectedInListOrder("available"), "available")}
          onMoveToAvailable={() => move(selectedInListOrder("selected"), "selected")}
          canMoveToSelected={selectedAvailableItems.length > 0}
          canMoveToAvailable={selectedChosenItems.length > 0}
        />
        <StatusTable
          id="selected"
          title={chosenTitle}
          emptyPlaceholder={chosenEmptyPlaceholder}
          statuses={chosenStatuses}
          selectedItems={selectedChosenItems}
          toggleSelection={toggle("selected")}
          isSortable={!!onReorder}
        />
      </div>
      {typeof document !== "undefined" &&
        createPortal(
          <DragOverlay zIndex={1000}>
            {activeDrag && activeStatus ? (
              <StatusItemPreview
                status={activeStatus}
                isSelected={selectionFor(activeDrag.container).includes(activeStatus.code)}
                count={getDragGroup(activeDrag.code, activeDrag.container).length}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
    </DndContext>
  );
};
